import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { setCellBackground } from '@platejs/table';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import textColorStyles from '../TextColorMenu/TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useRecentColors } from '../../hooks/useRecentColors';

// Same light-tint palette as the inline text-highlight menu so the editor
// has a single color vocabulary; reuse the styles too.
const PALETTE = ['#ffffff', '#f1f1ef', '#f4eee4', '#fbecdd', '#fbf3db', '#edf3ec', '#e7f3f8', '#f4f0f7', '#f9ecf2', '#fdebec'];

// Caret-in-cell lookup. Used to read the CURRENT cell's bg for the swatch
// preview when there's no multi-cell selection.
function findCellEntry(editor: any) {
    try {
        const at = editor?.selection;
        if (!at) return null;
        const entry = editor?.api?.above?.({ at, match: (n: any) => n?.type === 'td' || n?.type === 'th' });
        return entry ?? null;
    } catch {
        return null;
    }
}

// Multi-cell selection detector. Uses Plate's public `api.table.isSelectingCell()`
// instead of poking at the private `_selectedCellIds` option — the latter wasn't
// re-triggering useEditorSelector, which kept the button disabled on multi-cell
// drag-select.
function isSelectingCell(editor: any): boolean {
    try {
        return Boolean(editor?.api?.table?.isSelectingCell?.() ?? editor?.getApi?.({ key: 'table' })?.table?.isSelectingCell?.());
    } catch {
        return false;
    }
}

export function CellBackgroundMenu() {
    const editor = useEditorRef();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    // See TextColorMenu for the rationale — same "OS color picker open"
    // guard so the popover doesn't close mid-interaction.
    const pickerActiveRef = useRef(false);
    // See TextColorMenu — push only the final picked colour to recents on blur.
    const pendingColorRef = useRef<string | null>(null);
    const [recents, pushRecent] = useRecentColors('plateEditor.recentColors.cellBg');

    const visibleRecents = useMemo(() => {
        const paletteSet = new Set(PALETTE.map((c) => c.toLowerCase()));
        return recents.filter((c) => !paletteSet.has(c.toLowerCase()));
    }, [recents]);

    // Enabled when EITHER the caret is inside a single cell OR a multi-cell
    // drag-selection is active. Single-cell case also reads the current
    // background to drive the swatch preview.
    const state: { inCell: boolean; cellBg: string } = useEditorSelector((ed: any) => {
        if (isSelectingCell(ed)) {
            return { inCell: true, cellBg: '' };
        }
        const entry = findCellEntry(ed);
        if (entry) {
            const node: any = entry[0];
            // Plate's setCellBackground writes `background`. We also fall
            // back to the legacy `backgroundColor` for cells set via our
            // earlier code (or loaded from the Slate-shape stored format).
            return { inCell: true, cellBg: node?.background ?? node?.backgroundColor ?? '' };
        }
        return { inCell: false, cellBg: '' };
    }, []);

    const { inCell, cellBg } = state;

    useEffect(() => {
        if (!open) return;
        function handleClickOutside(e: any) {
            if (pickerActiveRef.current) return;
            if (wrapRef.current && !wrapRef.current.contains(e.target)) closePopover();
        }
        function handleEsc(e: any) {
            if (e.key === 'Escape') closePopover();
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Multi-cell: Plate's setCellBackground paints every selected cell at
    // once (pass selectedCells explicitly — leaving it implicit no-ops in
    // v53 when there's no active drag-selection).
    // Single-cell: setCellBackground silently no-ops without selectedCells,
    // so we fall back to a direct setNodes on the caret's cell path. Write
    // the same key (`background`) as Plate's setCellBackground so both paths
    // land in the same prop and the TableCellElement renderer (which reads
    // background ?? backgroundColor) picks it up. Also clear the legacy
    // `backgroundColor` so a re-painted cell doesn't keep its stale tint.
    //
    // CLEAR semantics: writing `background: 'transparent'` (rather than
    // deleting the prop) is load-bearing for HEADER cells (`<th>`) — the
    // base CSS sets `th { background: #f7f6f3 }`, so removing the inline
    // style would let that default come back through. An explicit
    // `transparent` overrides the CSS default and yields a truly
    // unstyled cell, matching the user expectation that "clear" makes the
    // header cell blend with the body cells.
    // Paint without closing — the native `<input type="color">` needs the
    // popover (and so itself) to stay mounted so `onBlur` can fire and push
    // the final hex to recents.
    const paintCell = (color: string | null) => {
        try {
            const ed: any = editor;
            const tableApi = ed?.api?.table ?? ed?.getApi?.({ key: 'table' })?.table;
            const selected = tableApi?.getSelectedCells?.();
            const bgValue = color ?? 'transparent';
            if (Array.isArray(selected) && selected.length > 1) {
                (setCellBackground as any)(ed, { color: bgValue, selectedCells: selected });
                // Wipe legacy field on every painted cell for parity with
                // the single-cell path.
                for (const cell of selected) {
                    try {
                        const path = (cell as any)?.[1];
                        if (path) ed?.tf?.setNodes?.({ backgroundColor: null }, { at: path });
                    } catch {
                        /* per-cell defensive */
                    }
                }
            } else {
                const entry = findCellEntry(ed);
                if (entry) {
                    const [, path] = entry;
                    ed?.tf?.setNodes?.({ background: bgValue, backgroundColor: null }, { at: path });
                }
            }
        } catch {
            /* defensive */
        }
    };

    const apply = (color: string | null) => (e: React.MouseEvent) => {
        e.preventDefault();
        paintCell(color);
        setOpen(false);
    };

    // Drop the picker-active flag on close so a stuck-true value can't
    // disable outside-click dismissal on the next open.
    const closePopover = () => {
        pickerActiveRef.current = false;
        setOpen(false);
    };

    const swatchColor = cellBg && cellBg !== '' ? cellBg : undefined;

    return (
        <div className={textColorStyles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive, !inCell && toolbarStyles.btnDisabled)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    if (!inCell) return;
                    setOpen((o) => !o);
                }}
                disabled={!inCell}
                title={inCell ? 'Cell background' : 'Place caret inside a table cell to color it'}
                aria-label="Cell background color"
                aria-haspopup="true"
                aria-expanded={open}
                style={swatchColor ? { color: swatchColor } : undefined}
            >
                <FormatColorFillIcon fontSize="small" />
            </button>
            {open && inCell && (
                <div className={textColorStyles.popover} role="menu" aria-label="Pick cell background">
                    <div className={textColorStyles.grid}>
                        {PALETTE.map((color) => (
                            <button
                                key={color}
                                type="button"
                                className={clsx(textColorStyles.swatchBtn, cellBg === color && textColorStyles.swatchBtnActive)}
                                role="menuitem"
                                onMouseDown={apply(color)}
                                title={color}
                                aria-label={`Cell background ${color}`}
                                style={{ background: color }}
                            />
                        ))}
                        <span className={textColorStyles.customWrap} title="Pick a custom cell color" aria-label="Custom cell color">
                            <input
                                type="color"
                                value={cellBg || '#fbecdd'}
                                onMouseDown={() => {
                                    pickerActiveRef.current = true;
                                    pendingColorRef.current = null;
                                }}
                                onInput={(e) => {
                                    const v = (e.target as HTMLInputElement).value;
                                    paintCell(v);
                                    pendingColorRef.current = v;
                                }}
                                onChange={(e) => {
                                    paintCell(e.target.value);
                                    pendingColorRef.current = e.target.value;
                                }}
                                onBlur={() => {
                                    if (pendingColorRef.current) {
                                        pushRecent(pendingColorRef.current);
                                        pendingColorRef.current = null;
                                    }
                                    setTimeout(() => {
                                        pickerActiveRef.current = false;
                                    }, 0);
                                    setOpen(false);
                                }}
                                className={textColorStyles.customInput}
                            />
                        </span>
                    </div>
                    {visibleRecents.length > 0 && (
                        <div className={textColorStyles.recents}>
                            <div className={textColorStyles.recentsLabel}>Recent</div>
                            {visibleRecents.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    className={clsx(textColorStyles.swatchBtn, cellBg === color && textColorStyles.swatchBtnActive)}
                                    role="menuitem"
                                    onMouseDown={apply(color)}
                                    title={color}
                                    aria-label={`Recent cell background ${color}`}
                                    style={{ background: color }}
                                />
                            ))}
                        </div>
                    )}
                    <button type="button" className={textColorStyles.clear} role="menuitem" onMouseDown={apply(null)}>
                        Clear cell color
                    </button>
                </div>
            )}
        </div>
    );
}
